import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  EMPTY,
  catchError,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  addCollectionItemToCollectionSuccess,
  barcodeCheckAddToCollection,
  barcodeCheckError,
  barcodeCheckSuccess,
  barcodeScanned,
  loadCollection,
  loadCollectionError,
  loadCollectionSuccess,
} from './collection.actions';
import { Store } from '@ngrx/store';
import { GlobalState } from '../../../store/store.model';
import { CollectionApiService } from '../services/collection-api.service';
import { collectionItems, scannedCollectionItemData } from './collection.selectors';
import { CollectionItem } from '../../collection-item/collection-item.model';
import { CollectionItemApiService } from '../../collection-item/services/collection-item-api.service';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { SuccessDialogComponent } from '../../../shared/components/success-dialog/success-dialog.component';

@Injectable()
export class CollectionEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<GlobalState>);
  private readonly collectionApiService = inject(CollectionApiService);
  private readonly collectionItemApiService = inject(CollectionItemApiService);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly dialog = inject(MatDialog);

  private readonly loadCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCollection),
      withLatestFrom(this.store.select(collectionItems)),
      switchMap(([{ reset }, loadedCollectionItems]) =>
        this.collectionApiService.getCollection().pipe(
          switchMap((collection) => {
            if (!collection) {
              return EMPTY;
            }

            const nextCollectionItems = collection.collectionItems ?? [];

            if (nextCollectionItems.length === 0) {
              return of(loadCollectionSuccess({ collection }));
            }

            const baseItems = reset ? [] : loadedCollectionItems ?? [];

            return this.loadCollectionItemImages(nextCollectionItems, baseItems).pipe(
              map((enrichedCollectionItems) =>
                loadCollectionSuccess({
                  collection: {
                    ...collection,
                    collectionItems: enrichedCollectionItems,
                  },
                }),
              ),
              catchError(() => of(loadCollectionError())),
            );
          }),
          catchError(() => of(loadCollectionError())),
        ),
      ),
    ),
  );

  private readonly barcodeScanned$ = createEffect(() =>
    this.actions$.pipe(
      ofType(barcodeScanned),
      withLatestFrom(this.store.select((state) => state.flow.guest)),
      switchMap(([{ barcode }, isGuest]) =>
        this.collectionApiService.scanQrCheck(barcode).pipe(
          switchMap((collectionItem) => {
            if (!collectionItem || !collectionItem.id) {
              return from(
                this.router.navigate(
                  isGuest ? ['/tabs/guest'] : ['/no-scan'],
                  { queryParams: { check: false } },
                ),
              ).pipe(
                switchMap(() => of(barcodeCheckError({}))),
              );
            }

            const successAction = barcodeCheckSuccess({ collectionItem });

            return from(
              this.router.navigate(
                isGuest
                  ? ['/tabs/guest']
                  : [`/tabs/collection-item/${collectionItem.id}`],
                {
                  queryParams: {
                    source: 'scan',
                    check: true,
                    alreadyAdded: collectionItem.alreadyAdded || false,
                  },
                },
              ),
            ).pipe(
              switchMap(() => of(successAction)),
              catchError(() => of(barcodeCheckError({}))),
            );
          }),
          catchError(() =>
            from(
              this.router.navigate(
                isGuest ? ['/tabs/guest'] : ['/no-scan'],
                { queryParams: { check: false } },
              ),
            ).pipe(
              switchMap(() => of(barcodeCheckError({}))),
            ),
          ),
        ),
      ),
    ),
  );

  private readonly barcodeCheckCollectionItemAddToCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(barcodeCheckAddToCollection),
      withLatestFrom(
        this.store.select(scannedCollectionItemData).pipe(filter((p) => !!p)),
      ),
      switchMap(([action, collectionItem]) =>
        this.collectionApiService
          .assignCollectionItem(collectionItem.token, action.massiveAssign)
          .pipe(
            switchMap((res) => {
              if (res.error) {
                return of(barcodeCheckError({ message: res.data.message }));
              }

              return [
                addCollectionItemToCollectionSuccess({
                  collectionItemId: res.id,
                  meta: action.meta,
                }),
                loadCollection({ loading: true, reset: false }),
              ];
            }),
            catchError((res) => of(barcodeCheckError(res))),
          ),
      ),
    ),
  );

  private readonly onAddCollectionItemSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addCollectionItemToCollectionSuccess),
        tap(({ meta }) => {
          switch (meta) {
            case 'create_location_and_assign_it':
              break;

            case 'add_without_location': {
              const resultDialog = this.dialog.open(SuccessDialogComponent, {
                data: {
                  message: this.translocoService.translate(
                    'collection_item.successfully_added',
                  ),
                },
              });

              resultDialog.afterClosed().subscribe(() => {
                void this.router.navigate(['/tabs/collection']);
              });
              break;
            }

            case 'assign_to_location':
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  private loadCollectionItemImages(
    nextCollectionItems: Partial<CollectionItem>[],
    loadedCollectionItems: Partial<CollectionItem>[],
  ): Observable<Partial<CollectionItem>[]> {
    const groupedByCode = new Map<string, Partial<CollectionItem>[]>();

    nextCollectionItems.forEach((item) => {
      if (!item.code) {
        return;
      }

      if (!groupedByCode.has(item.code)) {
        groupedByCode.set(item.code, []);
      }

      groupedByCode.get(item.code)!.push(item);
    });

    const imageRequests = Array.from(groupedByCode.entries()).map(([code, items]) => {
      const firstItem = items[0];
      const alreadyLoaded = loadedCollectionItems.find((p) => p.code === code);
      const alreadyHasBlob = alreadyLoaded?.image?.startsWith('blob:');

      if (firstItem.image && !alreadyHasBlob) {
        return this.collectionItemApiService.getCollectionItemImage(firstItem.image).pipe(
          map((blob) => {
            const imageUrl = URL.createObjectURL(blob);

            return items.map((item) => ({
              ...item,
              image: imageUrl,
            }));
          }),
          catchError(() => of(items)),
        );
      }

      const image = alreadyLoaded?.image ?? firstItem.image;

      return of(
        items.map((item) => ({
          ...item,
          image,
        })),
      );
    });

    return forkJoin(imageRequests).pipe(
      map((results) => results.flat()),
    );
  }
}
